'use client';

import { useState } from 'react';

import R2Image from '@/components/ui/R2Image';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';

import { CardsProps } from './types';

export default function Cards({
  title,
  testType,
  price,
  description = [],
  subDescription = [],
  notes = [],
  hasButton = false,
  defaultOpen = false,
  onEnquire = () => {},
}: CardsProps) {
  const [open, setOpen] = useState(defaultOpen);
  const isNumber = !isNaN(Number(price));
  const displayPrice =
    isNumber && price ? Number(price).toLocaleString() : price;

  return (
    <div
      data-card
      className="mb-3 flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-md"
    >
      <p className="text-theme-brand-blue text-sm font-bold">Scan</p>

      <p className="text-text-strong text-2xl font-bold">{title}</p>

      <div className="mt-6">
        <div className="flex">
          <R2Image
            path="/images/fibroscan/no-referral-required.svg"
            alt="Test type"
            objectFit="contain"
            containerClassName="relative w-5 h-5 shrink-0"
          />
          <div className="text-theme-brand-blue ml-2 text-base font-semibold">
            {testType}
          </div>
        </div>
      </div>

      <div
        className={`flex items-center justify-between ${open ? 'pt-4' : 'mt-4'}`}
      >
        <div className="flex items-center">
          <R2Image
            path="/images/fibroscan/File-text-icon.svg"
            alt="Included"
            objectFit="contain"
            containerClassName="relative w-5 h-5 shrink-0"
          />
          <div className="text-theme-brand-blue ml-2 text-base font-semibold">
            Included
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="mt-2">
          {description.length > 0 && (
            <ul
              className="font-regular text-text-list ml-2 space-y-1.5 text-base"
              data-testid="description-list"
            >
              {description.map((item, i) => (
                <li key={i} className="mt-1.5 flex items-center gap-2">
                  <R2Image
                    path="/images/fibroscan/check-icon.svg"
                    alt="check"
                    objectFit="contain"
                    containerClassName="relative w-5 h-5 shrink-0"
                  />
                  {item}
                </li>
              ))}
            </ul>
          )}

          {subDescription.length > 0 && (
            <div className="relative mt-2 ml-2">
              <ul
                className="font-regular text-text-list space-y-1.5 text-base"
                data-testid="sub-description-list"
              >
                {subDescription.map((item, i) => (
                  <li key={i} className={i > 0 && !open ? 'hidden' : ''}>
                    {i === 0 ? (
                      <div className="flex items-center gap-2">
                        <R2Image
                          path="/images/fibroscan/check-icon.svg"
                          alt="check"
                          objectFit="contain"
                          containerClassName="relative w-5 h-5 shrink-0"
                        />
                        {item}
                      </div>
                    ) : (
                      <span className="ml-6 flex items-center gap-2">
                        <span className="bg-surface-list-marker h-1.5 w-1.5 shrink-0 rounded-full" />
                        {item}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => setOpen((v) => !v)}
                className="font-regular text-theme-primary absolute top-0 right-0 flex cursor-pointer items-center gap-2 text-base"
              >
                {open ? 'View Less' : 'View All'}
                <svg
                  className={`h-4 w-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="mt-auto">
          {notes.length > 0 && (
            <div className="bg-surface-light mt-4 rounded-lg p-4">
              <div className="mb-4 flex items-center">
                <R2Image
                  path="/images/fibroscan/grey-info-icon.svg"
                  alt="Info"
                  objectFit="contain"
                  containerClassName="relative w-5 h-5 shrink-0"
                />
                <p className="text-text-list ml-2 text-base font-bold">
                  Please note:
                </p>
              </div>
              <ul className="font-regular text-text-list ml-2 list-disc space-y-1 pl-5 text-sm">
                {notes.map((note, i) => {
                  const boldMatch = note.match(/^(.*?)(\*\*(.+?)\*\*)(.*)$/);
                  if (boldMatch) {
                    return (
                      <li key={i}>
                        {boldMatch[1]}
                        <strong className="text-theme-primary text-[20px] font-bold">
                          {boldMatch[3]}
                        </strong>
                        {boldMatch[4]}
                      </li>
                    );
                  }
                  return <li key={i}>{note}</li>;
                })}
              </ul>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-4">
            {hasButton ? (
              <PrimaryButton
                data={{
                  title: 'Book your liver check today',
                  functionality: 'book_appointment',
                }}
              />
            ) : (
              <button
                onClick={onEnquire}
                className="bg-theme-primary cursor-pointer rounded-lg px-7 py-4 text-base font-normal! text-white transition hover:bg-indigo-800 active:bg-indigo-900"
              >
                Enquire Now
              </button>
            )}

            {isNumber ? (
              <div className="ml-2 flex items-center gap-2 whitespace-nowrap">
                <div className="text-theme-primary text-[26px] font-bold">
                  £{displayPrice}
                </div>
              </div>
            ) : (
              <div className="text-theme-primary text-[26px] font-bold whitespace-nowrap">
                {displayPrice}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
